import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ReadyState } from 'react-use-websocket';
import { usePcsClient } from 'src/context/pcs-auth-context';
import { usePcsUserEventSocket } from './socketHooks';
import { usePcsUserEventSocketHolodeckHub } from './socketHooksHolodeckHub';

const MINUTES_20_IN_SECONDS = 20 * 60;
const CID_AGENT_LIVE_VIEWING = 1171;
const CID_RECORDING_VIDEO = 1170;

export function useGetVideoToken(
  sid: number,
  startTimestamp: number,
  hasPcsMetadata: boolean
) {
  const client = usePcsClient();
  const { data } = useQuery(
    ['pcs_camera_token', sid, startTimestamp],
    () => client(`camera/token-camera/${sid}/${startTimestamp}`),
    {
      select: (_data) => _data.token,
      enabled: sid != null && !hasPcsMetadata,
    }
  );

  return data;
}

export function useGetSecondaryEvents(
  sid: number,
  triggerEventId: number,
  startTimestamp: number
) {
  const client = usePcsClient();
  const endTimestamp = startTimestamp + MINUTES_20_IN_SECONDS;
  const query = useQuery(
    ['pcs_secondary_events', sid, triggerEventId],
    () =>
      client(
        `subscriptions/${sid}/events?start=${startTimestamp}&end=${endTimestamp}`,
        {
          enabled: sid != null,
        }
      ),
    { enabled: !!sid && !!triggerEventId && !!startTimestamp }
  );

  const events = query.data?.events || [];
  const secondaryEvents = events.filter(
    (event) =>
      (event.eventCid === CID_AGENT_LIVE_VIEWING ||
        (event.eventCid === CID_RECORDING_VIDEO &&
          event.pcsMetadata != null)) &&
      event.eventId !== triggerEventId
  );
  return secondaryEvents;
}

export function useGetMediatorStreamingUrl(
  videoUrl?: string,
  agentToken?: string
) {
  const { enqueueSnackbar } = useSnackbar();
  return useQuery(
    ['streaming_url', videoUrl, agentToken],
    () =>
      axios.get<{ streamingSessionURL: string }>(videoUrl, {
        headers: {
          Authorization: `Bearer ${agentToken}`,
        },
      }),
    {
      enabled: videoUrl != null && agentToken != null,
      select: (data) => data.data.streamingSessionURL,
      onError: (error: any) => {
        enqueueSnackbar(
          `Error getting streaming ${error.response.data.statusCode}`,
          {
            variant: 'error',
          }
        );
      },
    }
  );
}

export function useGetPcsEventQuery(eventId: number) {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();

  // Control which backend Agent Portal connects to with this socket - pcs-admin or Holodeck Hub
  const enableHolodeckHubSocket =
    process.env.REACT_APP_ENABLE_HOLODECK_HUB_SOCKETS === 'true';

  // Attempt to use Holodeck Hub socket, with fallback to pcs-admin socket
  // socket.io for Holodeck Hub takes a few seconds to connect, and prior to that does not have `connected==true`, or any state to indicate "initialization"
  //  So we also need to track if socket.io has started a connection attempt, but not yet completed it: `attemptingToConnect`
  const { holodeckHubSocket, attemptingToConnect } =
    usePcsUserEventSocketHolodeckHub({
      eventId,
      enabled: enableHolodeckHubSocket,
    });
  const { readyState } = usePcsUserEventSocket(
    { event_id: eventId },
    !enableHolodeckHubSocket ||
      (!holodeckHubSocket?.connected && !attemptingToConnect)
  );

  const query = useQuery(
    ['pcs_events', eventId],
    () => client(`events/event/${eventId}`),
    {
      refetchInterval: readyState !== ReadyState.OPEN ? 2000 : null,
      enabled: eventId > 0,
      onError: (error) => {
        enqueueSnackbar(`Endpoint: events/event/{eventId}. ${error}`, {
          variant: 'error',
        });
      },
    }
  );
  return query;
}

export function useGetEventDocumentation(eventId: number) {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();
  const query = useQuery(
    ['event_documentation', eventId],
    () => client(`events/${eventId}/documentation`),
    {
      onError: (error: any) => {
        if (error.response.status === 404) {
          return;
        }
        enqueueSnackbar('Could not fetch documentation');
      },
      enabled: eventId != null,
    }
  );

  return query;
}

export function useListPcsEventsQuery(params: any, refetchInterval?: number) {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();
  const query = useQuery(
    ['pcs_events', params],
    () => client(`events/list`, { params: params }),
    {
      onError: (error) => {
        enqueueSnackbar(`Endpoint: events/list. ${error}`, {
          variant: 'error',
        });
      },
      refetchInterval,
    }
  );
  return query;
}

export function useFlagEventForReview(eventId: number) {
  const client = usePcsClient();
  const cache = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const mutation = useMutation(
    (data: { flag: boolean }) =>
      client(`events/${eventId}/flag-ai`, {
        method: 'POST',
        data,
      }),
    {
      onSuccess: () => {
        cache.invalidateQueries(['pcs_events', eventId]);
      },
      onError: (error) => {
        enqueueSnackbar(`Error saving flag for ai review: ${error}`, {
          variant: 'error',
        });
      },
    }
  );

  return mutation;
}

/** DETECTIONS */
export function useSetDetectionFlagMutation(eventId: number) {
  const client = usePcsClient();
  const cache = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    (data: {
      category: string;
      detectionId: string;
      flag: string;
      state: boolean;
    }) =>
      client(
        `events/update/${eventId}/detection/${data.category}/${data.detectionId}?flag=${data.flag}&state=${data.state}`,
        {
          method: 'POST',
          // data: data
        }
      ),
    {
      onSuccess: () => {
        cache.invalidateQueries(['pcs_events', eventId]);
      },
      onError: (error, variables) => {
        enqueueSnackbar(`Error saving ${variables.category}: ${error}`, {
          variant: 'error',
        });
      },
    }
  );

  return mutation;
}

export function useSetEventNewDetection() {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();
  // const cache = useQueryClient();
  const mutation = useMutation(
    (data: {
      eventId: number;
      pts_seconds: number;
      annotation: any;
      image: string;
    }) =>
      client(`events/update/${data.eventId}/new-detection`, {
        method: 'POST',
        data: {
          pts_seconds: data.pts_seconds,
          annotation: data.annotation,
          image: data.image,
        },
      }),
    {
      onError: (error) => {
        enqueueSnackbar(
          `Endpoint: events/update/{eventId}/new-detection. ${error}`,
          {
            variant: 'error',
          }
        );
      },
      // onSuccess: (data, variables) => {
      //   cache.invalidateQueries('motion_zone');
      //   cache.setQueryData(
      //     ['motion_zone', variables.sid, variables.uuid],
      //     data
      //   );
      // }
    }
  );

  return mutation;
}

// CLUSTERS
export function useGetPcsEventCluster(clusterId: string) {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();

  return useQuery(
    ['reid_clusters', clusterId],
    () => client(`events/cluster/${clusterId}`),
    {
      onError: (error) => {
        enqueueSnackbar(`Endpoint: events/cluster/{clusterId}. ${error}`, {
          variant: 'error',
        });
      },
    }
  );
}

export function useAuthorizeCluster() {
  const client = usePcsClient();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation(
    (data: { clusterId: string; authorized_time: number }) =>
      client(`events/authorize-cluster/${data.clusterId}`, {
        method: 'POST',
        params: {
          authorized_time: data.authorized_time,
        },
      }),
    {
      onError: (error) => {
        enqueueSnackbar(
          `Endpoint: events/authorize-cluster/{clusterId}. ${error}`,
          {
            variant: 'error',
          }
        );
      },
    }
  );

  return mutation;
}
